import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";
import { ERROR_TYPE } from "@/utils";

// Signal Priority Level enum - matches backend SignalPriorityLevel
export enum SignalPriorityLevel {
	PRIORITY_1 = "1",
	PRIORITY_2 = "2",
	PRIORITY_3 = "3",
	DISQUALIFIED = "disqualified",
}

// Priority item schema - multiple items per priority level
export const SignalPriorityItemSchema = z.object({
	id: z.number().optional(),
	priorityLevel: z.nativeEnum(SignalPriorityLevel),
	title: z.string().min(1, ERROR_TYPE.REQUIRED),
	definition: z.string().min(1, ERROR_TYPE.REQUIRED),
	examples: z.array(z.string()).min(1, "At least one example is required"),
	order: z.number().default(0),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type SignalPriorityItem = z.infer<typeof SignalPriorityItemSchema>;

// Company-level Signal Configuration Schema (template)
export const CompanySignalConfigSchema = z.object({
	id: z.number().nullable().optional(),
	companyId: z.number(),

	// AI Prompts
	deepResearchPrompt: z.string().default(""),
	systemPrompt: z.string().default(""),

	// Signal categorization prompts
	leadUpdatesPrompt: z.string().default(""),
	companyUpdatesPrompt: z.string().default(""),
	industryUpdatesPrompt: z.string().default(""),

	// Research options
	checkLinkedinPosts: z.boolean().default(true),
	analyzeLinkedinEngagement: z.boolean().default(true),
	checkTechStackChanges: z.boolean().default(true),

	// AI output language (ISO code); null = English
	language: z.string().nullable().optional(),

	// Priority definitions
	priorities: z.array(SignalPriorityItemSchema).default([]),

	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
});

export type CompanySignalConfig = z.infer<typeof CompanySignalConfigSchema>;

// Schema for creating/updating company signal config (request body)
export const UpdateCompanySignalConfigSchema = z.object({
	deepResearchPrompt: z.string().optional(),
	systemPrompt: z.string().optional(),
	leadUpdatesPrompt: z.string().optional(),
	companyUpdatesPrompt: z.string().optional(),
	industryUpdatesPrompt: z.string().optional(),
	checkLinkedinPosts: z.boolean().optional(),
	analyzeLinkedinEngagement: z.boolean().optional(),
	checkTechStackChanges: z.boolean().optional(),
	language: z.string().nullable().optional(),
	priorities: z
		.array(
			z.object({
				id: z.number().optional(),
				priorityLevel: z.nativeEnum(SignalPriorityLevel),
				title: z.string().min(1, ERROR_TYPE.REQUIRED),
				definition: z.string().min(1, ERROR_TYPE.REQUIRED),
				examples: z.array(z.string()).min(1, "At least one example is required"),
				order: z.number(),
			})
		)
		.optional(),
});

export type UpdateCompanySignalConfig = z.infer<typeof UpdateCompanySignalConfigSchema>;

// Schema for creating/updating priority item (used in modal)
export const CreateSignalPriorityItemSchema = z.object({
	priorityLevel: z.nativeEnum(SignalPriorityLevel),
	title: z.string().min(1, ERROR_TYPE.REQUIRED),
	definition: z.string().min(1, ERROR_TYPE.REQUIRED),
	examples: z.array(z.string()).min(1, "At least one example is required"),
	order: z.number(),
});

export type CreateSignalPriorityItem = z.infer<typeof CreateSignalPriorityItemSchema>;

// === Test Signal Config (Dry Run) Schemas ===

// Inline config sent for testing (mirrors UpdateCompanySignalConfig but no id)
export const InlineSignalConfigSchema = z.object({
	deepResearchPrompt: z.string().optional(),
	systemPrompt: z.string().optional(),
	leadUpdatesPrompt: z.string().optional(),
	companyUpdatesPrompt: z.string().optional(),
	industryUpdatesPrompt: z.string().optional(),
	language: z.string().nullable().optional(),
	priorities: z
		.array(
			z.object({
				id: z.number().optional(),
				priorityLevel: z.nativeEnum(SignalPriorityLevel),
				title: z.string(),
				definition: z.string(),
				examples: z.array(z.string()),
				order: z.number(),
			})
		)
		.optional(),
});

export type InlineSignalConfig = z.infer<typeof InlineSignalConfigSchema>;

// Request body for POST /signal/config/test
export const TestSignalConfigRequestSchema = z.object({
	listId: z.number().optional(),
	signalConfig: InlineSignalConfigSchema.optional(),
});

export type TestSignalConfigRequest = z.infer<typeof TestSignalConfigRequestSchema>;

// Response types
export const RawSignalSchema = z.object({
	title: z.string(),
	summary: z.string(),
	source_url: z.string().optional(),
	published_date: z.string().optional(),
});

export type RawSignal = z.infer<typeof RawSignalSchema>;

export const ScoredSignalSchema = z.object({
	title: z.string(),
	summary: z.string(),
	source_url: z.string().optional(),
	published_date: z.string().optional(),
	signal_category: z.string(),
	priority: z.string(),
	reasoning: z.string(),
});

export type ScoredSignal = z.infer<typeof ScoredSignalSchema>;

export const TestSignalConfigResponseSchema = z.object({
	selectedOrganization: z.object({
		id: z.number(),
		name: z.string(),
		websiteUrl: z.string().nullable(),
		source: z.enum(["tracker", "scored", "enriched", "own_company", "random"]),
		qualityScore: z.number(),
	}),
	warning: z.string().optional(),
	configSource: z.enum(["inline", "list", "company"]),
	rawSignals: z.array(RawSignalSchema),
	scoredSignals: z.array(ScoredSignalSchema),
	summary: z.object({
		totalSignals: z.number(),
		byPriority: z.object({
			prio1: z.number(),
			prio2: z.number(),
			prio3: z.number(),
			disqualified: z.number(),
		}),
		byCategory: z.object({
			leadUpdate: z.number(),
			companyUpdate: z.number(),
			industryUpdate: z.number(),
		}),
	}),
});

export type TestSignalConfigResponse = z.infer<typeof TestSignalConfigResponseSchema>;

// === Signal Priority History Schemas ===

export const SignalPriorityHistoryUserSchema = z.object({
	id: z.number(),
	firstName: z.string(),
	lastName: z.string(),
});

export type SignalPriorityHistoryUser = z.infer<typeof SignalPriorityHistoryUserSchema>;

export const SignalPriorityHistoryItemSchema = z.object({
	id: z.number(),
	trackerSignalResultId: z.number(),
	previousPriority: z.string().nullable(),
	newPriority: z.string(),
	userId: z.number(),
	user: SignalPriorityHistoryUserSchema,
	signalTitle: z.string().nullable(),
	signalCategory: z.string().nullable(),
	organizationName: z.string().nullable(),
	createdAt: z.string(),
});

export type SignalPriorityHistoryItem = z.infer<typeof SignalPriorityHistoryItemSchema>;

export const SignalPriorityHistoryResponseSchema = z.object({
	data: z.array(SignalPriorityHistoryItemSchema),
	meta: MetaDataSchema,
});

export type SignalPriorityHistoryResponse = z.infer<typeof SignalPriorityHistoryResponseSchema>;

export interface SignalPriorityHistoryParams {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
}
